/**
 * Supplier Variant Sold Exception Config
 *
 * Covers: ex-product-ls-supplier-variant-sold
 *
 * Child variants carrying a Sales Channel = Supplier option that have
 * transacted at POS in the last 30 days. Supplier variants are
 * wholesale/receiving packs and should not sell — a sale usually
 * indicates a till/setup error or misconfigured variant group.
 */
import { formatCurrency, formatDate } from '../../../utils/inspectorFormatters'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import {
  activePart,
  composeRowSubtitle,
  datePart,
  moneyPart,
  skuPart,
  textPart,
} from '../shared/rowSubtitle'
import { MAIN_CATEGORY_COLUMN } from '../shared/productExceptionColumns'
import { formatMainCategory } from '../../../utils/productCategory'
import { productStatusSortValue, renderProductName, renderProductStatus } from '../shared/productRenderers'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'

const PRODUCT_NAME_FROZEN_WIDTH = 350

const columns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  buildIssuesColumn(),
  {
    key: 'name',
    label: 'Product Name',
    align: 'left',
    width: PRODUCT_NAME_FROZEN_WIDTH,
    minWidth: PRODUCT_NAME_FROZEN_WIDTH,
    freeze: 'left',
    render: renderProductName,
    link: { field: 'lightspeed_product_link', system: 'lightspeed' },
  },
  {
    key: 'active',
    label: 'Product Status',
    align: 'center',
    render: renderProductStatus,
    getSortValue: productStatusSortValue,
  },
  {
    key: 'sku',
    label: 'SKU',
    render: (value) => value || 'N/A',
  },
  MAIN_CATEGORY_COLUMN,
  {
    key: 'last_sold',
    label: 'Last Sold',
    render: (value, _row, ctx) => ctx.variant === 'inspector' ? formatDate(value) : (value || '-'),
    exportFormat: 'date',
  },
  {
    key: 'price_including_tax',
    label: 'Price',
    type: 'currency',
    align: 'right',
    render: (value, _row, ctx) => ctx.variant === 'inspector' ? formatCurrency(value) : undefined,
    exportFormat: 'currency',
  },
  {
    key: 'supply_price',
    label: 'Supply Price',
    type: 'currency',
    align: 'right',
    render: (value, _row, ctx) => ctx.variant === 'inspector' ? formatCurrency(value) : undefined,
    exportFormat: 'currency',
  },
]

export const supplierVariantSoldConfig: ExceptionConfig = {
  codes: ['ex-product-ls-supplier-variant-sold'],
  columns,
  defaultSortColumn: 'last_sold',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      skuPart(row.sku),
      activePart(row.active ?? row.is_active),
      row.last_sold ? `last sold ${datePart(row.last_sold)}` : null,
      textPart(formatMainCategory(row.category_root_name)),
      moneyPart(row.price_excluding_tax ?? row.price_excl_tax),
    ]),
}
